// COP — OUTBOUND CoT EGRESS client (the COP→/emit transit glue).
//
// The COP authors a chat message; cap-chat publishes the (sealed) CanonicalEnvelope onto
// the in-process EnvelopeBus AND — when egress is armed — hands it to THIS client, which
// POSTs it as JSON to the same-origin /emit route (caddy → the tak-gateway emit HTTP server
// on :8675, active only when the gateway runs with EMIT_COT=1). The gateway builds the
// marked-placeholder CoT (clear outer + the sealed `<__tdf>` inner) and injects it onto the
// TAK net. The clear chat <remarks> shows the marked placeholder; the real body rides the
// sealed inner (docs/AOA-ATAK-INTEROP.md §4 Seam A, TAK-CHAT-SEMANTICS §8.4).
//
// THE GATE (default OFF — the lowest-risk choice): egress is attempted ONLY when explicitly
// armed, so normal dev / the ?mock=1 sandbox NEVER spam failed POSTs at a gateway that isn't
// running. Two opt-ins, EITHER arms it:
//   - VITE_EMIT=1            — the build/env flag the live-scenario stack sets (scripts/…).
//   - ?emit=1 URL param      — a per-session opt-in for a live-crossing check w/o a rebuild.
// When DISARMED, makeChatEmit returns `undefined` so cap-chat installs NO egress hook and the
// behaviour is byte-identical to before this seam existed (the in-process chat is untouched).
//
// FAIL-SOFT BY CONSTRUCTION: the returned hook is fire-and-forget — it NEVER throws into the
// chat send and NEVER rejects unhandled. A network error / 404 / non-2xx is logged concisely
// (once-throttled per failure) and swallowed. cap-chat's local publish has already happened
// and ALWAYS stands, egress success or not.
//
// CONTRACT:S1-TAK-GATEWAY.1.0
// references CONTRACT:P1-CANONICAL-ENVELOPE.1.0 (the posted body shape).

import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
import type { ChatEmitFn } from "../../../../packages/cap-chat/src/index.ts";
import { freshAccessToken } from "./kas.ts";

/** Injectable token getter for the /emit persona-bearer stamp. `force` mirrors the kas.ts
 *  reactive-401 pattern (force ONE refresh before the single retry). Must resolve to "" when
 *  no session exists (no header is stamped then — the gateway 401s, we stay fail-soft). */
export type EmitTokenGetter = (force?: boolean) => Promise<string>;

/** The same-origin egress route (caddy `/emit` → gateway :8675). Same-origin = no CORS, no
 *  mixed-content on the HTTPS COP origin (mirrors /feed → :8674). */
export const EMIT_PATH = "/emit";

/** Is outbound egress ARMED? Default OFF. Armed by EITHER the VITE_EMIT build flag (the
 *  live-scenario stack) OR a ?emit=1 URL opt-in (a per-session live-crossing check). */
export function emitArmed(): boolean {
  // VITE_EMIT=1 — the build/env flag (import.meta.env is statically replaced by Vite; the
  // optional chain keeps this safe under a non-Vite tsx check where import.meta.env is absent).
  const viteFlag = (import.meta.env?.VITE_EMIT as string | undefined) === "1";
  // ?emit=1 — a per-session URL opt-in (guarded for a non-DOM/headless context).
  let urlFlag = false;
  try {
    urlFlag =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("emit") === "1";
  } catch {
    urlFlag = false;
  }
  return viteFlag || urlFlag;
}

/** Build the egress URL (same-origin /emit through caddy). Kept a function so a non-DOM
 *  (tsx/test) context that never calls it doesn't trip on `window`. */
function emitUrl(): string {
  // Same-origin: rely on the page origin so nothing hardcodes a backend port (port-resilient,
  // exactly like feed.ts derives wss://<host>/feed).
  return EMIT_PATH;
}

/**
 * makeChatEmit — build the cap-chat egress hook, or `undefined` when egress is DISARMED.
 *
 * When armed, returns a ChatEmitFn that POSTs the envelope JSON to /emit. The hook is
 * SYNCHRONOUS-RETURNING + fire-and-forget: it kicks off the fetch and returns immediately, so
 * cap-chat (which calls it after the local publish and never awaits it) is never blocked. Any
 * failure is swallowed + logged — the local in-process chat is the source of truth.
 *
 * @param doFetch  injectable fetch (defaults to the global) — tests pass a spy.
 * @param armed    injectable gate (defaults to emitArmed()) — tests force it on/off.
 * @param getToken injectable persona-token getter (defaults to kas.ts freshAccessToken) —
 *                 the /emit gateway now REQUIRES a persona bearer (fail-closed 401 without
 *                 one); the stamp reuses the exact KAS silent-refresh path, zero new state.
 */
export function makeChatEmit(
  doFetch: typeof fetch = (...a: Parameters<typeof fetch>) => fetch(...a),
  armed: boolean = emitArmed(),
  getToken: EmitTokenGetter = freshAccessToken,
): ChatEmitFn | undefined {
  if (!armed) return undefined;
  const url = emitUrl();
  return (env: CanonicalEnvelope): void => {
    // Fire-and-forget. We DELIBERATELY do not return/await the promise into cap-chat — egress
    // must never gate or block a chat send. The .catch() guarantees no unhandled rejection.
    void postEnvelope(doFetch, url, env, getToken);
  };
}

/** POST one envelope to /emit, fail-soft. Resolves always (never rejects): a network error or
 *  a non-2xx response is logged concisely + swallowed (the local publish already stands).
 *
 *  PERSONA BEARER: stamps `Authorization: Bearer <token>` when the getter yields a non-empty
 *  token (the gateway's /emit auth gate verifies it against the IdP JWKS). On a 401 — the one
 *  failure a refresh can cure — force ONE refresh and retry ONCE (mirrors the reactive-401
 *  backstop in kas.ts decryptTdf); a second failure is logged + swallowed like every other. */
async function postEnvelope(
  doFetch: typeof fetch,
  url: string,
  env: CanonicalEnvelope,
  getToken: EmitTokenGetter,
): Promise<void> {
  try {
    const send = async (token: string): Promise<Response> =>
      await doFetch(url, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          // Stamp the persona bearer only when a session token exists — an empty header
          // would be malformed; without one the gateway 401s and we swallow (fail-soft).
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(env),
      });

    // The token getter must never break egress: any getter failure degrades to "no header"
    // (the gateway then 401s and the local publish still stands — fail-soft, never fail-open).
    let token = "";
    try {
      token = (await getToken(false)) ?? "";
    } catch {
      token = "";
    }
    let res = await send(token);

    // REACTIVE 401 RETRY (once): the proactive refresh can lose to clock skew or a token that
    // expired mid-flight. Force ONE refresh; retry ONLY if it yielded a genuinely new token.
    if (res.status === 401) {
      let fresh = "";
      try {
        fresh = (await getToken(true)) ?? "";
      } catch {
        fresh = "";
      }
      if (fresh && fresh !== token) res = await send(fresh);
    }

    if (!res.ok) {
      // The gateway fail-closes (401/403 auth, 422/400 marking); a 404 means EMIT_COT!=1 / no
      // route. Either way: log once, swallow — the in-process chat is unaffected.
      // eslint-disable-next-line no-console
      console.warn(`[cop-emit] /emit returned ${res.status} for ${env.id} — swallowed (local publish stands)`);
    }
  } catch (e) {
    // Network error (gateway down, offline). Concise log, swallowed.
    // eslint-disable-next-line no-console
    console.warn(`[cop-emit] /emit POST failed for ${env.id} — swallowed (local publish stands):`, (e as Error).message);
  }
}
